import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppHeader } from "@/components/AppHeader";
import { InputPanel } from "@/components/InputPanel";
import { FloatingStreak } from "@/components/FloatingStreak";
import { FloatingPro } from "@/components/FloatingPro";
import { RateLimitBanner } from "@/components/RateLimitBanner";
import { SolutionView, type SolutionStep } from "@/components/SolutionView";
import { FollowUpThread } from "@/components/FollowUpThread";
import { useAuth } from "@/hooks/useAuth";
import { solveProblem, saveSolvedProblem, explainStepDeeper, RateLimitError, type SolutionData } from "@/lib/solve";
import { useLanguage } from "@/hooks/useLanguage";
import { LANGUAGE_NAMES } from "@/lib/languages";
import { updateStreak } from "@/lib/streak";
import { PremiumOfferModal } from "@/components/PremiumOfferModal";
import { TrialStatusCard } from "@/components/TrialStatusCard";
import { TrialOfferCard } from "@/components/TrialOfferCard";
import { usePremiumGate } from "@/hooks/usePremiumGate";
import { Sparkles, Clock, Target, Languages, AlertTriangle } from "lucide-react";
import { PremiumHomepage } from "@/components/homepage/PremiumHomepage";
import { AdConsentBanner } from "@/components/AdConsentBanner";
import { Button } from "@/components/ui/button";
import { AdBanner } from "@/components/AdBanner";
import { OnboardingTour } from "@/components/OnboardingTour";
import { trackProblemSolved } from "@/lib/gtag";
import { track } from "@/lib/analytics";
import { useTranslation } from "react-i18next";
import { StudyAllowance } from "@/components/StudyAllowance";
import { FirstAnswerPremiumNote, firstAnswerNoteKey } from "@/components/FirstAnswerPremiumNote";
import { studyFunnelSignal, trackEmailCampaignReturn } from "@/lib/study-conversion";
import { remainingDailyQuestions } from "@/lib/study-accounting-policy";

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    meta: [
      { title: "Inquira AI — Meet Your AI Study Superpower" },
      {
        name: "description",
        content:
          "Snap a photo of any problem and get clear, step-by-step solutions. Learn how to solve it, not just the answer.",
      },
      { property: "og:title", content: "Inquira AI — Meet Your AI Study Superpower" },
      { property: "og:description", content: "Snap a photo of any problem and get clear, step-by-step solutions in 70+ languages." },
      { property: "og:url", content: "https://www.inquira.online/" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Inquira - Your AI Study Companion" },
      { name: "twitter:description", content: "Snap a photo of any problem and get clear, step-by-step solutions in 70+ languages." },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/" }],
  }),
});

function Index() {
  const { user, isLoading: authLoading, scanCount, scanLimit, isPaid, streak, premium, questionUsage, refreshUsage, refreshPremium, applyServerUsage, applyStreak, signOut } = useAuth();
  const { promptUpgrade } = usePremiumGate();
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState<string | null>(null);
  const [solution, setSolution] = useState<SolutionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [languageSuggestionDismissed, setLanguageSuggestionDismissed] = useState(false);
  const [rateLimit, setRateLimit] = useState<{ retryAfter: number; message: string } | null>(null);
  const [showOffer, setShowOffer] = useState(false);
  const [showFirstNote, setShowFirstNote] = useState(false);
  useEffect(() => { studyFunnelSignal("landing_visit"); }, []);
  useEffect(() => { if (user) void trackEmailCampaignReturn(); }, [user?.id]);

  // New accounts get the one-time 3-day Premium trial offer on first visit.
  useEffect(() => {
    if (!premium?.authenticated) return;
    if (premium.premium_access || premium.trial_used || premium.premium_offer_seen_at) return;
    setShowOffer(true);
    void track("premium_offer_shown", { surface: "home" });
  }, [premium]);

  const handleSubmit = async (input: string, type: "text" | "image") => {
    if (!user) return;

    setError(null);
    setRateLimit(null);
    setCurrentQuestion(type === "text" ? input : "📷 Image problem");
    setIsLoading(true);
    try {
      const result = await solveProblem(input, type, language.code);
      setSolution(result);
      setShowSolution(true);
      trackProblemSolved();
      if (!isPaid) {
        try {
          const key = firstAnswerNoteKey(user.id);
          if (!localStorage.getItem(key)) { localStorage.setItem(key, "1"); setShowFirstNote(true); }
        } catch { /* storage unavailable: skip the note */ }
      }
      void track("problem_solved", { input_type: type, language: language.code });

      await saveSolvedProblem(user.id, type === "text" ? input : "Image problem", type, result);
      const newStreak = await updateStreak(user.id);
      if (newStreak !== null) applyStreak(newStreak);

      // Prefer authoritative server-reported usage from the solve response;
      // fall back to a full refresh so UI can never drift from quota enforcement.
      if (result.usage) {
        applyServerUsage(result.usage);
      } else {
        await refreshUsage();
      }
    } catch (err) {
      if (err instanceof RateLimitError) {
        setRateLimit({ retryAfter: err.retryAfterSeconds, message: err.message });
        void track("solve_rate_limited", { input_type: type });
      } else {
        setError(err instanceof Error ? err.message : "Failed to solve problem");
        void track("solve_failed", { input_type: type });
      }
      // Even on failure, resync from server in case the scan was counted.
      await refreshUsage();
    } finally {
      setIsLoading(false);
    }
  };

  const perDayLimit = Number.isFinite(scanLimit) ? scanLimit : Infinity;
  const remainingCount = questionUsage?.enforcementEnabled ? (remainingDailyQuestions(questionUsage) ?? Infinity) : Math.max(0, perDayLimit - scanCount);
  const scansRemaining = Number.isFinite(remainingCount) ? `${remainingCount}` : "∞";
  const limitReached = remainingCount === 0 && (!questionUsage?.creditsEnabled || questionUsage.credits <= (questionUsage.reservedCredits ?? 0));

  if (!authLoading && !user) {
    return (
      <>
        <PremiumHomepage />
        <AdConsentBanner />
      </>
    );
  }

  return (
    <div className="min-h-screen notebook-lines">
      {showOffer && (
        <PremiumOfferModal
          onClose={() => setShowOffer(false)}
          onTrialStarted={() => refreshPremium()}
        />
      )}
      <div className="max-w-lg mx-auto px-4 pb-24">
        <OnboardingTour />
        <AppHeader onSignOut={signOut} user={user} isPaid={isPaid} />
        <FloatingStreak streak={streak} />
        <FloatingPro isPaid={isPaid} />
        <h1 className="sr-only">Inquira — AI study companion for step-by-step problem solving</h1>
        <main>
        {user && <StudyAllowance refreshKey={scanCount} />}
        {premium && <div className="mb-4"><TrialStatusCard state={premium} /></div>}
        {!isPaid && (
          <div className="mb-4">
            <TrialOfferCard state={premium} surface="home_banner" />
          </div>
        )}
        {!showSolution ? (
          <div className="space-y-6">
            {!isPaid && (
              <div
                className={`rounded-xl p-4 border flex items-start gap-3 ${
                  limitReached
                    ? "bg-destructive/10 border-destructive/30 text-destructive"
                    : remainingCount <= 2
                      ? "bg-neon-yellow/20 border-neon-yellow/40 text-ink"
                      : "bg-neon-blue/5 border-neon-blue/20 text-ink"
                }`}
              >
                {limitReached ? (
                  <AlertTriangle className="size-5 shrink-0 mt-0.5" />
                ) : (
                  <Sparkles className="size-5 shrink-0 mt-0.5 text-neon-pink" />
                )}
                <div className="flex-1 text-sm">
                  {limitReached ? (
                    <>
                      <p className="font-bold">{t("home.dailyLimitReached")}</p>
                      <p className="text-xs mt-0.5 text-destructive/80">
                        {t("home.dailyLimitReachedDetail")}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-bold">
                        {t("home.scansRemaining", { count: remainingCount })}
                      </p>
                      <p className="text-xs mt-0.5 text-ink-muted">
                        {t("home.freeResetsAtMidnight")}
                      </p>
                    </>
                  )}
                </div>
                <Button
                  variant={limitReached ? "scan" : "subtle"}
                  size="sm"
                  className="shrink-0"
                  onClick={() => promptUpgrade(limitReached ? "scan_limit" : "generic", "daily_scans")}
                >
                  {t("common.upgrade")}
                </Button>
              </div>
            )}

            <InputPanel
              onSubmit={handleSubmit}
              isLoading={isLoading}
              disabled={limitReached || !!rateLimit}
              disabledReason={
                rateLimit
                  ? "Rate limited — please wait for the countdown."
                  : limitReached
                    ? t("home.dailyLimitReachedShort")
                    : undefined
              }
            />

            {rateLimit && (
              <RateLimitBanner
                retryAfterSeconds={rateLimit.retryAfter}
                message={rateLimit.message}
                onReady={() => { /* keep banner visible so user knows they can retry */ }}
                onDismiss={() => setRateLimit(null)}
              />
            )}

            {error && (
              <div className="bg-destructive/10 text-destructive text-sm rounded-xl p-4 border border-destructive/20 text-center">
                {error}
                {!isPaid && (
                  <Link to="/upgrade" className="mt-2 block text-xs text-neon-pink font-semibold underline">
                    {t("home.upgradeToProLink")}
                  </Link>
                )}
              </div>
            )}

            {!isLoading && (
              <div className="grid grid-cols-3 gap-3">
                <QuickStat
                  icon={<Sparkles className="size-4" />}
                  label={t("monetization.ai_questions_remaining_today")}
                  value={Number.isFinite(remainingCount) ? scansRemaining : t("monetization.premium_benefits_preserved")}
                />
                <QuickStat
                  icon={<Clock className="size-4" />}
                  label={t("common.today")}
                  value={`${scanCount} ${t("home.solved")}`}
                />
                <QuickStat
                  icon={<Target className="size-4" />}
                  label={t("common.plan")}
                  value={questionUsage ? (questionUsage.premium ? "Premium" : "Free Forever") : t("common.loading")}
                />
              </div>
            )}

            <AdBanner slot="HOME_FEED_1" format="horizontal" isPaid={isPaid} />

            {!isLoading && (
              <nav aria-label="Study guides" className="pt-2 text-center text-xs text-ink-muted">
                <span>Popular guides: </span>
                <Link to="/algebra-ai-solver" className="text-neon-pink font-semibold underline">
                  Algebra AI Solver
                </Link>
                <span> · </span>
                <Link to="/calculus-ai-solver" className="text-neon-pink font-semibold underline">
                  Calculus AI Solver
                </Link>
              </nav>
            )}
            {isLoading && (
              <div className="flex flex-col items-center gap-4 py-8">
                <div className="size-16 rounded-full bg-neon-pink/10 flex items-center justify-center animate-pulse">
                  <Sparkles className="size-7 text-neon-pink" />
                </div>
                <div className="text-center space-y-1">
                  <p className="font-[family-name:var(--font-display)] font-bold text-ink">
                    {t("home.analyzing")}
                  </p>
                  <p className="text-sm text-ink-muted">
                    {t("home.analyzingSubtitle")}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : solution ? (
          <div className="space-y-6">
            {currentQuestion && (
              <div className="flex gap-3 items-start">
                <div className="size-8 rounded-full bg-brand-light flex items-center justify-center text-xs font-bold text-brand shrink-0">
                  {t("home.you")}
                </div>
                <div className="bg-card rounded-2xl rounded-tl-sm px-4 py-3 border border-rule shadow-sm">
                  <p className="text-sm font-medium text-ink">
                    {currentQuestion}
                  </p>
                </div>
              </div>
            )}

            {/* Auto-detect language suggestion */}
            {solution.detectedLanguage && solution.detectedLanguage !== language.code && !languageSuggestionDismissed && (
              <div className="bg-neon-blue/10 border border-neon-blue/20 rounded-xl p-3 flex items-center gap-3">
                <Languages className="size-5 text-neon-blue shrink-0" />
                <p className="text-sm text-ink flex-1">
                  We detected <strong>{LANGUAGE_NAMES[solution.detectedLanguage] || solution.detectedLanguage}</strong> input. Switch explanations to match?
                </p>
                <Button
                  variant="subtle"
                  size="sm"
                  className="text-xs shrink-0"
                  onClick={() => {
                    if (solution.detectedLanguage) setLanguage(solution.detectedLanguage);
                    setLanguageSuggestionDismissed(true);
                  }}
                >
                  {t("home.switch")}
                </Button>
                <button
                  className="text-xs text-ink-muted hover:text-ink"
                  onClick={() => setLanguageSuggestionDismissed(true)}
                >
                  {t("home.dismiss")}
                </button>
              </div>
            )}

            {showFirstNote && !isPaid && <FirstAnswerPremiumNote onDismiss={() => setShowFirstNote(false)} />}
            <SolutionView
              isPaid={isPaid}
              language={language.code}
              concept={solution.concept}
              conceptDescription={solution.conceptDescription}
              steps={solution.steps as SolutionStep[]}
              finalAnswer={solution.finalAnswer}
              summary={solution.summary}
              detectedLanguage={solution.detectedLanguage}
              originalConcept={solution.originalConcept}
              originalConceptDescription={solution.originalConceptDescription}
              originalSteps={solution.originalSteps as SolutionStep[] | undefined}
              originalFinalAnswer={solution.originalFinalAnswer}
              originalSummary={solution.originalSummary}
              onPracticeSimilar={() => {
                setShowSolution(false);
                setCurrentQuestion(null);
                setSolution(null);
              }}
              onExplainDeeper={async (_stepIndex, step) => {
                const result = await explainStepDeeper({
                  stepTitle: step.title,
                  stepExplanation: step.explanation,
                  stepWork: step.work,
                  originalQuestion: currentQuestion ?? undefined,
                  outputLanguage: language.code,
                });
                return result.deeperExplanation;
              }}
            />

            <FollowUpThread
              key={currentQuestion ?? "solution"}
              solution={solution}
              originalQuestion={currentQuestion ?? undefined}
              outputLanguage={language.code}
            />

            <AdBanner slot="SOLUTION_BOTTOM_1" format="rectangle" isPaid={isPaid} />

            <div className="pt-4">
              <button
                onClick={() => {
                  setShowSolution(false);
                  setCurrentQuestion(null);
                  setSolution(null);
                  setError(null);
                }}
                className="w-full py-3 rounded-xl border-2 border-dashed border-rule text-sm font-[family-name:var(--font-display)] font-bold text-ink-muted hover:border-neon-pink hover:text-neon-pink transition-colors"
              >
                {t("home.newProblem")}
              </button>
            </div>
          </div>
        ) : null}
        </main>
      </div>
    </div>
  );
}

function QuickStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="bg-card rounded-xl p-3 border border-rule shadow-sm text-center space-y-1">
      <div className="flex items-center justify-center text-ink-muted">
        {icon}
      </div>
      <p className="text-xs text-ink-muted">{label}</p>
      <p className="text-xs font-bold text-ink">{value}</p>
    </div>
  );
}
